import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { Field, Fields, formValueSelector, reduxForm, SubmissionError } from 'redux-form';
import { Helmet } from 'react-helmet';
import { isEmpty as _isEmpty } from 'lodash';
import { InputText } from '@saucelabs/sl-component-form';
import { Grid } from '@saucelabs/sl-component-grid';
import { Loader } from '@saucelabs/sl-component-loader';
import { Tabs, Tab } from '@saucelabs/sl-component-tabs';
import { recordTrack as recordTrackAction } from '@saucelabs/sl-util-segmentio';
import validate from '@saucelabs/sl-util-validate';
import { selectors as concurrencySelectors } from '@saucelabs/sl-module-concurrency';

import * as actions from '../../actions';
import * as utils from '../../utils';
import configuratorSelector from '../../selectors';
import { FORM_TAB_STATES, FORM_TYPES } from '../../constants';
import Styles from './WebTestingForm.styles';
import RealDevicesContainer from './RealDevicesContainer';
import DesktopBrowserConfigurator from '../../components/DesktopBrowserConfigurator';
import VirtualDeviceConfigurator from '../../components/VirtualDeviceConfigurator';
import ConfigsContainer from './ConfigsContainer';

const constraints = {
  [FORM_TYPES.DESKTOP_BROWSER]: {
    browserConfiguration: {
      presence: { allowEmpty: false },
    },
  },
  [FORM_TYPES.REAL_DEVICE]: {
    deviceConfiguration: {
      presence: { allowEmpty: false },
    },
  },
  [FORM_TYPES.VIRTUAL_DEVICE]: {
    virtualDeviceConfiguration: {
      presence: { allowEmpty: false },
    },
  },
};

const getFormType = (formValues) => {
  const { platformType, device, deviceType } = formValues;

  return utils.getFormType(platformType, device, deviceType);
};

const getFormErrors = (formValues) => {
  const formType = getFormType(formValues);

  if (!formType) {
    return {};
  }

  return validate(formValues, constraints[formType]);
};

class WebTestingForm extends Component {
  componentDidMount() {
    const { recordTrack } = this.props;
    recordTrack('Manual Testing Form Launched');
  }

  componentDidUpdate() {
    this.checkIfCurrentTunnelIsStillRunning();
  }

  checkIfCurrentTunnelIsStillRunning = () => {
    const { change, formValues, tunnels } = this.props;

    if (!formValues.tunnel) {
      return;
    }

    if (!utils.isTunnelStillRunning(formValues.tunnel, tunnels)) {
      change('tunnel', null);
    }
  }

  validateDevice = (device) => {
    const {
      formValues,
      sauceConnectPublicRdcEnabled,
      showSauceConnectPublicRdcDisabledNotification,
    } = this.props;
    const publicDevice = !device.isPrivate();
    const sauceConnectRequested = !!formValues.tunnelIdentifier;
    const sauceConnectPublicDevicesDisabled = !sauceConnectPublicRdcEnabled;

    if (publicDevice && sauceConnectRequested && sauceConnectPublicDevicesDisabled) {
      showSauceConnectPublicRdcDisabledNotification();
      return false;
    }

    return true;
  }

  handleFormSubmit = (values) => {
    const { onSubmit } = this.props;
    const errors = getFormErrors(values);

    if (errors) {
      throw new SubmissionError(errors);
    }
    onSubmit(values);
  }

  renderURLInput = (wideScreenPosition) => {
    const className = wideScreenPosition ? 'url-wrapper-header' : 'url-wrapper-content';
    return (
      <div className={Styles[className]}>
        <div className={Styles['url-positioning-helper']}>
          <Field
            className="form-control"
            component={InputText}
            id="url"
            label="URL"
            name="url"
            placeholder="URL you want to test"
            type="text"
          />
        </div>
      </div>
    );
  }

  getActiveTabKey = () => {
    const { formValues } = this.props;
    const { device, deviceType } = formValues;

    if (device === FORM_TAB_STATES.DEVICE.MOBILE) {
      if (deviceType === FORM_TAB_STATES.DEVICE_TYPE.VIRTUAL) {
        return FORM_TAB_STATES.WEB_DEVICE_TYPE.MOBILE_VIRTUAL;
      }
      if (deviceType === FORM_TAB_STATES.DEVICE_TYPE.REAL) {
        return FORM_TAB_STATES.WEB_DEVICE_TYPE.MOBILE_REAL;
      }
    }
    return FORM_TAB_STATES.WEB_DEVICE_TYPE.DESKTOP;
  }

  render() {
    const {
      browsers,
      change,
      clearSubmitErrors,
      dispatch,
      formValues,
      handleSubmit,
      initialize,
      recentConfigs,
      savedConfigs,
      tunnels,
      userConcurrency,
    } = this.props;
    const currentAllocated = userConcurrency.get('current').overall;
    const allowedToAllocate = userConcurrency.get('allowed').overall;
    const cannotStartSession = [currentAllocated, allowedToAllocate].every(Number.isFinite) &&
      currentAllocated >= allowedToAllocate;
    const submitDisabled = !!getFormErrors(formValues) || cannotStartSession;

    const onTabSelectHandler = (webDeviceType) => {
      switch (webDeviceType) {
        case FORM_TAB_STATES.WEB_DEVICE_TYPE.DESKTOP:
          change('device', FORM_TAB_STATES.DEVICE.DESKTOP);
          change('deviceType', FORM_TAB_STATES.DEVICE_TYPE.VIRTUAL);
          break;
        case FORM_TAB_STATES.WEB_DEVICE_TYPE.MOBILE_REAL:
          change('device', FORM_TAB_STATES.DEVICE.MOBILE);
          change('deviceType', FORM_TAB_STATES.DEVICE_TYPE.REAL);
          break;
        case FORM_TAB_STATES.WEB_DEVICE_TYPE.MOBILE_VIRTUAL:
          change('device', FORM_TAB_STATES.DEVICE.MOBILE);
          change('deviceType', FORM_TAB_STATES.DEVICE_TYPE.VIRTUAL);
          break;
        default:
          break;
      }
      change('deviceConfiguration', null);
      dispatch(clearSubmitErrors());
    };

    return (
      <form
        className={Styles.default}
        onSubmit={handleSubmit(this.handleFormSubmit)}
      >
        <Helmet>
          <title>Live Web Testing</title>
        </Helmet>
        <Grid
          className={Styles['main-container']}
          fluid
          tagName="section"
        >
          <Tabs
            activeKey={this.getActiveTabKey()}
            animation={false}
            className={Styles['tabs-container']}
            defaultActiveKey={FORM_TAB_STATES.WEB_DEVICE_TYPE.DESKTOP}
            id="web-testing-tabs"
            mountOnEnter
            onSelect={onTabSelectHandler}
          >
            <Tab
              eventKey={FORM_TAB_STATES.WEB_DEVICE_TYPE.DESKTOP}
              mountOnEnter
              title="Desktop"
            >
              <Loader
                className={Styles.loader}
                fontSize={3}
                show={_isEmpty(browsers)}
              >
                <div>
                  {this.renderURLInput(false)}
                  <Fields
                    browsers={browsers}
                    component={DesktopBrowserConfigurator}
                    names={[
                      'browserConfiguration',
                      'tunnel',
                    ]}
                    tunnels={tunnels}
                  />
                </div>
              </Loader>
            </Tab>
            <Tab
              eventKey={FORM_TAB_STATES.WEB_DEVICE_TYPE.MOBILE_REAL}
              mountOnEnter
              title="Mobile Real"
            >
              <div>
                {this.renderURLInput(false)}
                <Field
                  component={RealDevicesContainer}
                  deviceConfiguration={formValues.deviceConfiguration || {}}
                  name="deviceConfiguration"
                  onDeviceSelect={(device) => {
                    const valid = this.validateDevice(device);
                    if (valid) {
                      change('deviceConfiguration', device);
                    }
                  }}
                  onTunnelChange={(tunnel) => change('tunnelIdentifier', tunnel)}
                />
              </div>
            </Tab>
            <Tab
              eventKey={FORM_TAB_STATES.WEB_DEVICE_TYPE.MOBILE_VIRTUAL}
              mountOnEnter
              title="Mobile Virtual"
            >
              <Loader
                className={Styles.loader}
                fontSize={3}
                show={_isEmpty(browsers)}
              >
                <div>
                  {this.renderURLInput(false)}
                  <Fields
                    browsers={browsers}
                    component={VirtualDeviceConfigurator}
                    names={[
                      'virtualDeviceConfiguration',
                      'tunnel',
                    ]}
                    tunnels={tunnels}
                  />
                </div>
              </Loader>
            </Tab>
          </Tabs>
          {this.renderURLInput(true)}
        </Grid>
        <Field
          component={ConfigsContainer}
          name="saveConfiguration"
          onItemClick={(config) => {
            initialize({
              ...formValues,
              ...utils.getLiveTestingConfig(config),
            });
          }}
          onToggleSaveConfig={(value) => {
            change('saveConfiguration', value);
          }}
          recentConfigs={recentConfigs}
          savedConfigs={savedConfigs}
          submitDisabled={submitDisabled}
        />
      </form>
    );
  }
}

WebTestingForm.propTypes = {
  browsers: PropTypes.object.isRequired,
  change: PropTypes.func.isRequired,
  clearSubmitErrors: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  formValues: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  recentConfigs: PropTypes.array.isRequired,
  recordTrack: PropTypes.func.isRequired,
  sauceConnectPublicRdcEnabled: PropTypes.bool.isRequired,
  savedConfigs: PropTypes.array.isRequired,
  showSauceConnectPublicRdcDisabledNotification: PropTypes.func.isRequired,
  tunnels: PropTypes.array.isRequired,
  userConcurrency: ImmutablePropTypes.map.isRequired,
};

/* we need to have an access to current values in form to handle Tabs state */
const mapStateToProps = (state) => {
  const defaultConfig = {
    browserConfiguration: {
      browser: 'edge',
      browserDisplay: 'Microsoft Edge',
      browserVersionDisplay: '15',
      device: 'Windows 10',
      res: '1024x768',
      os: 'Windows 10',
      osDisplay: 'Windows 10',
      osVersionDisplay: 'Windows 10',
      version: '15',
    },
  };

  const lastConfig = configuratorSelector(state).lastConfigWeb(defaultConfig);

  const isLastConfigTunnelRunning = utils.isTunnelStillRunning(
    lastConfig.tunnel,
    configuratorSelector(state).tunnels,
  );

  return ({
    browsers: configuratorSelector(state).browsers,
    recentConfigs: configuratorSelector(state).recentConfigsWeb,
    savedConfigs: configuratorSelector(state).savedConfigsWeb,
    sauceConnectPublicRdcEnabled: configuratorSelector(state).sauceConnectPublicRdcEnabled,
    userConcurrency: concurrencySelectors.concurrencySelector()(state).concurrency,
    tunnels: configuratorSelector(state).tunnels,
    formValues: formValueSelector('manualWebTesting')(
      state,
      'platformType',
      'device',
      'deviceType',
      'url',
      'browserConfiguration',
      'deviceConfiguration',
      'virtualDeviceConfiguration',
      'tunnel',
      'tunnelIdentifier',
    ),
    initialValues: {
      assets: true,
      device: FORM_TAB_STATES.DEVICE.DESKTOP,
      deviceType: FORM_TAB_STATES.DEVICE_TYPE.REAL,
      ...lastConfig,
      platformType: FORM_TAB_STATES.PLATFORM_TYPE.WEB,
      tunnel: isLastConfigTunnelRunning ? lastConfig.tunnel : null,
    },
  });
};

const mapDispatchToProps = {
  onSubmit: actions.handleTabsFormSubmit,
  showSauceConnectPublicRdcDisabledNotification: actions.showSauceConnectRdcPublicDisabled,
  recordTrack: recordTrackAction,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(reduxForm({
  form: 'manualWebTesting',
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  updateUnregisteredFields: true,
  touchOnChange: true,
})(WebTestingForm));
export { WebTestingForm };
